import type { Vocabulary } from '@scribe/core';

/**
 * Language choices offered in the UI. A convenience list, NOT an API constraint —
 * `openapi/scribe.yaml` keeps `JobSpec.language` a free-form string on purpose, since
 * Transcribe's real code list is long and changes (plan-vocabulary-ux.md §"New file").
 *
 * Deliberately TWO languages, not the 10–14 that plan suggested. Product decision
 * (2026-08-20): English, Spanish, and Combined are the whole offering for now, expanding to
 * "English / Other Language / Combined" later. Adding a language here is a one-line change and
 * everything downstream — the mode list, the vocabulary filter, the stored preference's
 * validation — derives from these constants rather than repeating them.
 */

/** Languages a vocabulary can be built for, and that a recording can be transcribed in. */
export const LANGUAGES = [
  { code: 'en-US', label: 'English' },
  { code: 'es-US', label: 'Spanish' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

/** Matches the backend's `clients.transcribe.DEFAULT_LANGUAGE`. An omitted `job_spec.language`
 *  means this, NOT auto-detect (plan-multilingual.md §4.4). */
export const DEFAULT_LANGUAGE: LanguageCode = 'en-US';

/**
 * `job_spec.language` for "this recording contains more than one language". A magic string in a
 * field that otherwise holds BCP-47 codes — the established convention on both sides
 * (`clients.transcribe._is_language_id_mode`), and cheaper than a second `language_mode` field
 * that would create two sources of truth for one decision.
 */
export const COMBINED_LANGUAGE = 'multi';

/** Every value the transcription-language control can produce. */
export type TranscriptionLanguage = LanguageCode | typeof COMBINED_LANGUAGE;

/**
 * The control's options, in offer order. Combined is last: it is the least common choice.
 */
export const TRANSCRIPTION_LANGUAGE_OPTIONS: ReadonlyArray<{
  value: TranscriptionLanguage;
  label: string;
}> = [
  ...LANGUAGES.map((l) => ({ value: l.code as TranscriptionLanguage, label: l.label })),
  { value: COMBINED_LANGUAGE, label: 'Combined (English + Spanish)' },
];

/** Candidate languages sent as `job_spec.language_options` for Combined. Exactly the two we
 *  offer — and exactly the backend's default, so this is belt-and-braces rather than load-
 *  bearing. Sent explicitly anyway so the persisted job_spec records what was actually meant,
 *  instead of depending on a server default that could later change. */
export const COMBINED_LANGUAGE_OPTIONS: readonly string[] = LANGUAGES.map((l) => l.code);

export function isTranscriptionLanguage(value: unknown): value is TranscriptionLanguage {
  return (
    value === COMBINED_LANGUAGE || LANGUAGES.some((l) => l.code === value)
  );
}

/** Human label for a stored/returned language value, falling back to the raw code so a
 *  recording transcribed with a language we no longer offer still renders sensibly. */
export function languageLabel(value: string): string {
  if (value === COMBINED_LANGUAGE) return 'Combined (English + Spanish)';
  return LANGUAGES.find((l) => l.code === value)?.label ?? value;
}

/**
 * Which vocabularies are usable with a given transcription language.
 *
 * A vocabulary has exactly one language, and AWS **rejects** a job that pairs it with a
 * different one — `BadRequestException: The language es-US used in the specified vocabulary …
 * doesn't match the specified language code` — which failed the job and stranded the recording
 * (plan-multilingual.md §1 Bug A). The server now keys the vocabulary to its own language, so
 * this filter is defence in depth; its real value is that a mismatch becomes **unpickable**
 * rather than picked-then-rejected.
 *
 * Combined accepts either, since `LanguageIdSettings` keys the chosen vocabulary to whichever
 * of the two candidate languages it belongs to.
 */
export function vocabularyMatchesLanguage(
  vocabularyLanguage: string,
  transcriptionLanguage: TranscriptionLanguage,
): boolean {
  if (transcriptionLanguage === COMBINED_LANGUAGE) {
    return COMBINED_LANGUAGE_OPTIONS.includes(vocabularyLanguage);
  }
  return vocabularyLanguage === transcriptionLanguage;
}

/** Published vocabularies whose language works with `language`.
 *
 * Published only (web-ui-issues #10): an unpublished vocabulary has no engine-side terms, so
 * offering it would bias nothing while looking like it did. Lives here rather than in the
 * component so `useTranscriptionPrefs` can apply the same filter without importing from a
 * component — the filter and the language rules belong together. */
export function selectableVocabularies(
  vocabularies: readonly Vocabulary[] | undefined,
  language: TranscriptionLanguage,
): Vocabulary[] {
  return (vocabularies ?? [])
    .filter((v) => v.published_version != null)
    // AWS has to actually hold it, READY. Published-but-unregistered was the state of EVERY
    // vocabulary before §2.6 step 2, and offering one meant the pipeline silently dropped it (or,
    // before step 1, failed the job and stranded the recording). See `vocabularyIsUsable`.
    .filter(vocabularyIsUsable)
    .filter((v) => vocabularyMatchesLanguage(v.language, language));
}

/** AWS state a vocabulary must be in before it will bias anything. */
export const VOCABULARY_READY = 'READY';

/**
 * Whether a vocabulary can actually be used on a job.
 *
 * `engine_state` is AWS's own answer, read live at list time. Anything other than `READY` — most
 * often `null`, meaning it was never registered or the lookup failed — means Transcribe would
 * reject a job naming it, and that rejection fails the whole job rather than just losing the term
 * bias. The pipeline applies the same rule (`start_transcribe` skips a non-READY vocabulary), so
 * this keeps the UI honest about what the backend will do rather than offering a choice that
 * silently does nothing.
 */
export function vocabularyIsUsable(vocabulary: Pick<Vocabulary, 'engine_state'>): boolean {
  return vocabulary.engine_state === VOCABULARY_READY;
}

/** Why a vocabulary cannot be used, for display next to it — or null when it can.
 *
 * Checks in the SAME order `selectableVocabularies` filters — publication first, then engine
 * state — so the explanation can never disagree with the decision. Reporting "ready" for a
 * vocabulary the picker then omits would be worse than saying nothing. */
export function vocabularyUnusableReason(
  vocabulary: Pick<Vocabulary, 'engine_state' | 'published_version'>,
): string | null {
  if (vocabulary.published_version == null) return 'Not published yet';
  if (vocabularyIsUsable(vocabulary)) return null;
  if (vocabulary.engine_state == null) return 'Not registered with the transcription engine';
  if (vocabulary.engine_state === 'PENDING') return 'Still being prepared';
  return `Unusable (${vocabulary.engine_state})`;
}
